import { useQueryClient } from '@tanstack/react-query';
import { Divider } from 'primereact/divider';
import { Skeleton } from 'primereact/skeleton';
import { Tag } from 'primereact/tag';
import { classNames } from 'primereact/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import GlobeIcon from '../../../../../../assets/icons/Globe';
import InfoIconPrimary from '../../../../../../assets/icons/InfoIconPrimary';
import LocationTreeSelect from '../../../../../../components/dialog-manager/dialogs/add-product-dialog/components/location-tree-select/LocationTreeSelect';
import TagsInplaceEditWithAutoComplete from '../../../../../../components/forms/tags-inplace-edit-auto-complete/TagsInplaceEditWithAutoComplete';
import TooltipIcon from '../../../../../../components/tooltip-icon/TooltipIcon';
import { useGetProductById } from '../../../../../../hooks/api/products';
import {
  AssignTagProps,
  UnassignTagProps,
  useAssignTagToEntity,
  useListTags,
  useUnassignTagFromEntity,
} from '../../../../../../hooks/api/tag';
import { useAuth } from '../../../../../../hooks/auth/useAuth';
import useNotificationContext from '../../../../../../hooks/notification/useNotificationContext';
import ActivePcfDetails from '../../../../../../shared/components/active-pcf-details/ActivePcfDetails';
import { mapProductToActivePcf } from '../../../../../../shared/components/active-pcf-details/data-utils';
import {
  MAX_TAG_LENGTH,
  MAX_TAGS_PER_PRODUCT,
} from '../../../../../../shared/constants/products.constant';
import {
  AssignEntityTagErrorcodes,
  AssignEntityTagErrorcodesTranslationKeyMap,
} from '../../../../../../shared/enums/assign-tag-entity-error-codes';
import { ToastSeverity } from '../../../../../../shared/enums/toast-severity';
import {
  UnassignEntityTagErrorcodes,
  UnassignEntityTagErrorcodesTranslationKeyMap,
} from '../../../../../../shared/enums/unassign-tag-entity-error-code';
import {
  CountryCodeToLabelMap,
  RegionCodeToLabelMap,
} from '../../../../../../shared/helpers/country-region-helpers';
import { EntityType, ITag } from '../../../../../../shared/interfaces/ITag';
import ProductInplaceEdit from '../../../product-inplace-edit/ProductInplaceEdit';
import styles from './ProductDetailsTab.module.css';

type ProductDetailsTabProps = {
  isEditingDisabled?: boolean;
};

const ProductDetailsTab = ({ isEditingDisabled = false }: ProductDetailsTabProps): JSX.Element => {
  const { t } = useTranslation();
  const { notify } = useNotificationContext();
  const { productId } = useParams() as { productId: string };
  const { user } = useAuth();

  const canUseTags = user?.company?.license !== 'basic';

  const { data: product, isLoading } = useGetProductById(productId);
  const { data: companyTags } = useListTags({ enabled: canUseTags });
  const queryClient = useQueryClient();

  const onUnassignSuccess = () => {
    queryClient.invalidateQueries(['products', `${productId}`]);
    queryClient.invalidateQueries(['products']);
  };

  const onAssignSuccess = () => {
    queryClient.invalidateQueries(['products', `${productId}`]);
    queryClient.invalidateQueries(['products']);
    queryClient.invalidateQueries(['tags']);
  };

  const onAssignError = async (error: Error, variable: AssignTagProps) => {
    const errorCode = error.message;

    let errorMessage = '';

    if (Object.values(AssignEntityTagErrorcodes).includes(errorCode as AssignEntityTagErrorcodes)) {
      // It's a known error code, handle it accordingly
      errorMessage = t(
        `toastMessages.assignTag.failure.${
          AssignEntityTagErrorcodesTranslationKeyMap[errorCode as AssignEntityTagErrorcodes]
        }`,
      );
    } else {
      // It's an unknown error code, show generic error message
      errorMessage = t('toastMessages.assignTag.failure.assignTags');
    }

    notify({
      severity: ToastSeverity.ERROR,
      detail: errorMessage,
    });

    // if the tag assignment fails then remove it from display
    const assignedTags = tags?.filter((item) => {
      return variable.name !== item.name;
    });
    setTags(assignedTags);
  };

  const onUnassignError = async (error: Error, variable: UnassignTagProps) => {
    const errorCode = error.message;

    let errorMessage = '';

    if (
      Object.values(UnassignEntityTagErrorcodes).includes(errorCode as UnassignEntityTagErrorcodes)
    ) {
      // It's a known error code, handle it accordingly
      errorMessage = t(
        `toastMessages.unAssignTag.failure.${
          UnassignEntityTagErrorcodesTranslationKeyMap[errorCode as UnassignEntityTagErrorcodes]
        }`,
      );
    } else {
      // It's an unknown error code, show generic error message
      errorMessage = t('toastMessages.unAssignTag.failure.unAssignTags');
    }

    notify({
      severity: ToastSeverity.ERROR,
      detail: errorMessage,
    });
    // if the tag unassignment fails then add it to display
    const removedTag = tags?.find((item) => {
      return variable.tagId === item.id;
    });
    if (removedTag) {
      setTags([...(tags ?? []), removedTag]);
    }
  };
  const { mutate: unassignTagMutation } = useUnassignTagFromEntity({
    onSuccess: onUnassignSuccess,
    onError: onUnassignError,
  });

  const { mutate: assignTagMutation } = useAssignTagToEntity({
    onSuccess: onAssignSuccess,
    onError: onAssignError,
  });

  const [newLocationValue, setNewLocationValue] = useState<string | undefined>(
    product?.country ?? product?.region,
  );

  const [tags, setTags] = useState<Partial<ITag>[]>([]);

  useEffect(() => {
    if (product?.tags) {
      setTags(product.tags);
    }
  }, [product]);

  // Listen for rollback events on the location field to reset newLocationValue
  useEffect(() => {
    const handleLocationRollback = () => {
      setNewLocationValue(product?.country ?? product?.region);
    };
    window.addEventListener('inplace-edit:location:rollback', handleLocationRollback);

    return () => {
      window.removeEventListener('inplace-edit:location:rollback', handleLocationRollback);
    };
  }, [product?.country, product?.region]);

  // Memoized variable to control the visibility of the location info icon based on active footprint id (if there is a published or not)
  const isLocationInfoIconVisible = useMemo(() => {
    return !!product?.pcf?.activeCarbonFootprintId;
  }, [product?.pcf?.activeCarbonFootprintId]);

  const getLocationText = (): string => {
    if (product?.country) {
      return CountryCodeToLabelMap[product?.country] || product?.country;
    }

    if (product?.region) {
      return RegionCodeToLabelMap[product?.region] || product?.region;
    }

    return 'UNKNOWN';
  };

  const getLocationIcon = (): JSX.Element | null => {
    if (product?.country && CountryCodeToLabelMap[product.country]) {
      return (
        <span
          className={classNames(
            `fi fi-${product.country.toLowerCase()} border-round align-items-center justify-content-center`,
            styles['flag-icon'],
          )}
        />
      );
    }

    if (product?.region) {
      return <GlobeIcon />;
    }

    return null;
  };

  const unassignTagFromProduct = (tag: Partial<ITag>) => {
    setTags(
      tags?.filter((item) => {
        return item.name !== tag.name;
      }),
    );
    const tagId = companyTags?.find((item) => {
      return item.name === tag.name;
    })?.id;
    if (tagId) {
      unassignTagMutation({
        entityId: Number(productId),
        entityType: EntityType.PRODUCT,
        tagId,
      });
    }
  };

  const assignTagFromProduct = async (tag: Partial<ITag>): Promise<void> => {
    if (!tag.name) {
      return;
    }
    // check if tag is already assigned
    const alreadyAssigned = tags?.find((item) => {
      return item.name === tag.name;
    });
    if (alreadyAssigned) {
      return;
    }

    // Checking whether the tag already belongs to company
    const existingTagId = companyTags?.find((item) => {
      return item.name === tag.name;
    })?.id;

    if (existingTagId) {
      setTags([...tags, tag]);
      assignTagMutation({
        entityId: Number(productId),
        entityType: EntityType.PRODUCT,
        name: `${tag.name}`,
      });
    } else {
      // if not adding validation to check whether the tag name length is valid
      if (tag.name.trim().length > MAX_TAG_LENGTH) {
        notify({
          severity: ToastSeverity.ERROR,
          detail: t('toastMessages.assignTag.failure.nameValidation'),
        });

        return;
      }
      setTags([...tags, { name: tag.name.trim().replace(/\s+/g, '-') }]);
      assignTagMutation({
        entityId: Number(productId),
        entityType: EntityType.PRODUCT,
        name: tag.name,
      });
    }
  };

  return (
    <div
      className={classNames(
        'flex flex-column gap-6 mt-2 md:mt-4 w-12 md:w-8',
        styles['product-details-tab'],
      )}
    >
      {/* General Details */}
      <div className='flex flex-column gap-4'>
        <h4 className='-mb-1'>
          {t('productDetailsPage.tabs.productDetailsTab.sections.generalDetailsSection.title')}
        </h4>

        <div className='grid grid-nogutter gap-4 md:gap-0'>
          {/* Product Id */}
          <div className='col-12 md:col-4 md:pr-6 flex flex-column'>
            <span className='font-medium'>
              {t(
                'productDetailsPage.tabs.productDetailsTab.sections.generalDetailsSection.productId',
              )}
            </span>
            {isLoading ? (
              <Skeleton height='1rem' className='my-1 py-3 w-full' />
            ) : (
              <>
                <ProductInplaceEdit
                  disabled={isEditingDisabled}
                  fieldId='productId'
                  value={product?.productId}
                  textClassname='font-light text-black-alpha-60'
                  displayClassname='font-light'
                  undefinedText={t('global.notSpecified')}
                />
              </>
            )}
          </div>

          {/* Location */}
          <div className='col-12 md:col-8 flex flex-column'>
            <span className='font-medium'>
              {t(
                'productDetailsPage.tabs.productDetailsTab.sections.generalDetailsSection.location',
              )}
            </span>

            {isLoading ? (
              <Skeleton height='1rem' className='my-1 py-3 w-full' />
            ) : (
              <div className='flex flex-row gap-2 align-items-center'>
                <div className={classNames(isLocationInfoIconVisible ? 'w-auto' : 'w-full')}>
                  {/* Adjust the width of InplaceEdit based on the visibility of the location info icon */}
                  <ProductInplaceEdit
                    disabled={isEditingDisabled || isLocationInfoIconVisible}
                    fieldId='location'
                    type='other'
                    value={newLocationValue}
                    textClassname='font-light text-black-alpha-60'
                    displayClassname='font-light'
                    undefinedText={t('global.notSpecified')}
                    customDisplay={
                      <div className='flex flex-row gap-2 line-height-1 align-items-center font-light'>
                        {getLocationIcon()}
                        {getLocationText()}
                      </div>
                    }
                    customContent={
                      <LocationTreeSelect
                        onValueSelect={(regionCode, countryCode) => {
                          setNewLocationValue(countryCode || regionCode);
                        }}
                        value={newLocationValue}
                        placeholder='Select location'
                        wrapperClassname='w-full'
                      />
                    }
                  />
                </div>

                {isLocationInfoIconVisible && (
                  <TooltipIcon
                    id='location'
                    tooltipText={t(
                      'productDetailsPage.tabs.productDetailsTab.sections.generalDetailsSection.locationDisabledTooltipText',
                    )}
                    icon={<InfoIconPrimary />}
                    position='right'
                  />
                )}
              </div>
            )}
            <span />
          </div>
        </div>

        {/* Product Tags */}
        {canUseTags && (
          <div className='flex flex-column align-items-start'>
            <span className='font-medium pb-2'>
              {t('productDetailsPage.tabs.productDetailsTab.sections.generalDetailsSection.tags')}
            </span>
            <div className='flex flex-wrap gap-2'>
              {tags?.map((item) => {
                return (
                  <Tag
                    className='bg-transparent text-900 text-sm font-normal px-2 py-1 border-1 border-round flex flex-row-reverse gap-1 white-space-nowrap'
                    value={item.name}
                    key={item.name}
                    icon={'pi pi-times text-xs font-bold'}
                    pt={{
                      icon: {
                        className: 'cursor-pointer',
                        onClick: () => {
                          unassignTagFromProduct(item);
                        },
                      },
                    }}
                  />
                );
              })}
            </div>

            {tags.length < MAX_TAGS_PER_PRODUCT && (
              <TagsInplaceEditWithAutoComplete
                options={companyTags ?? []}
                placeholder={t(
                  'productDetailsPage.tabs.productDetailsTab.sections.generalDetailsSection.addTagsPlaceholder',
                )}
                selectedOptions={product?.tags ?? []}
                buttonLabel={t(
                  'productDetailsPage.tabs.productDetailsTab.sections.generalDetailsSection.addTags',
                )}
                assignTagToEntity={assignTagFromProduct}
              />
            )}
          </div>
        )}

        {/* Product Description */}
        <div className='flex flex-column'>
          <span className='font-medium'>
            {t(
              'productDetailsPage.tabs.productDetailsTab.sections.generalDetailsSection.productDescription',
            )}
          </span>

          {isLoading ? (
            <Skeleton height='4rem' className='my-1 py-3 w-full' />
          ) : (
            <ProductInplaceEdit
              disabled={isEditingDisabled}
              fieldId='description'
              value={product?.description}
              textClassname='font-light text-black-alpha-60'
              displayClassname='font-light'
              undefinedText={t('global.notSpecified')}
              isTextArea
              optional
            />
          )}
        </div>
      </div>

      <Divider className='p-0 m-0 visible md:hidden' />

      {/* Active PCF */}
      {product?.pcf ? (
        <ActivePcfDetails data={mapProductToActivePcf(product)} isLoading={isLoading} />
      ) : (
        <div className='flex flex-column gap-4'>
          <h4 className='-mb-1'>{t('activePcfSection.title')}</h4>
          <span className='font-light text-black-alpha-60'>
            {t('activePcfSection.noActivePcfText')}
          </span>
        </div>
      )}
    </div>
  );
};

export default ProductDetailsTab;
